import React, { useEffect, useRef, useState } from 'react'
import { useErrorService, ABSpinner } from '@abroad/components'
import { useParams } from 'react-router-dom'
import API from '../../utils/API'
import QuestVideos from './QuestVideos'
import QuestBlogs from './QuestBlogs'
import JourneysLibrary from '../journeys/JourneysLibrary'
import LearningListenAttached from './LearningListenAttached'
import { JourneyMediasContext } from '../../utils/contexts'
import useDebounce from '../common/useDebounce'

const LearningListenPicker = ({ questName, mediasList, setMediasList }) => {
  const listenCount = mediasList?.length || 0
  const attachedLabel =
    listenCount === 1 ? '1 attached' : `${listenCount} attached`

  return (
    <div className='experience-audio-picker'>
      <div className='experience-audio-picker-attached'>
        <div className='experience-ap-head'>
          <h4>On this experience</h4>
          <span className='experience-ap-count'>{attachedLabel}</span>
        </div>
        <div className='experience-ap-body'>
          {listenCount === 0 ? (
            <div className='experience-ap-empty'>
              <p>Nothing attached yet</p>
              <small>Add audio from the library on the right.</small>
            </div>
          ) : (
            <LearningListenAttached />
          )}
        </div>
      </div>
      <div className='experience-audio-picker-divider' aria-hidden='true' />
      <div className='experience-audio-picker-library'>
        <div className='experience-ap-head'>
          <h4>Library</h4>
        </div>
        <div className='experience-ap-body'>
          <JourneysLibrary
            title='Library'
            journeyType='quest'
            hideTitle
            experienceChrome
          />
        </div>
      </div>
    </div>
  )
}

const QuestsLearning = ({
  questName,
  onCompletenessChange,
  onSaveStateChange,
}) => {
  const Error = useErrorService()
  const [mediasList, setMediasListState] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isMediaFetching, setIsMediaFetching] = useState(true)
  const [resource, setResource] = useState()
  const [activeTab, setActiveTab] = useState('listen')
  const { questId } = useParams()
  const initialLoadRef = useRef(true)
  const listenHydratedRef = useRef(false)
  const isReorderingRef = useRef(false)
  const mediasListRef = useRef([])
  const lastSavedKeyRef = useRef(null)
  const debouncedMedias = useDebounce(mediasList, 800)

  const setMediasList = (updater) => {
    setMediasListState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      mediasListRef.current = next
      return next
    })
  }

  const getListKey = (list) =>
    (list || [])
      .map((media) => `${media.mediaOrModuleId || media._id}:${media.type}`)
      .join(',')

  const setSaveState = (state) => {
    if (typeof onSaveStateChange === 'function') {
      onSaveStateChange(state)
    }
  }

  useEffect(() => {
    const getUserJourneyMedia = async () => {
      try {
        const { data } = await API.AdminAPI.quests.getQuestJourney({
          questId,
        })
        const audios = data?.audios?.map((item) => ({
          ...item.mediaOrModuleDetail,
          type: item.type,
          mediaOrModuleId: item.mediaOrModuleId,
        }))

        if (data) {
          const list = Object.keys(data).length === 0 ? [] : audios
          setMediasList(list)
          lastSavedKeyRef.current = getListKey(list)
        }
      } catch (e) {
        Error.showError(e)
      }
      setIsMediaFetching(false)
    }
    getUserJourneyMedia()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questId])

  // Show "Saving…" as soon as Listen attachments change (before debounce fires).
  // Skip while reordering — persist only after drop.
  useEffect(() => {
    if (isMediaFetching) return
    if (!listenHydratedRef.current) {
      listenHydratedRef.current = true
      return
    }
    if (isReorderingRef.current) return
    setSaveState('saving')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediasList, isMediaFetching])

  const saveQuestJourney = async (list) => {
    const key = getListKey(list)
    if (key === lastSavedKeyRef.current) {
      setSaveState('saved')
      return
    }
    setSaveState('saving')
    try {
      const medias = (list || []).map((media) => ({
        mediaOrModuleId: media.mediaOrModuleId,
        type: media.type,
      }))
      const journeyData = {
        audios: medias,
      }
      await API.AdminAPI.quests.updateQuestJourney(questId, journeyData)
      lastSavedKeyRef.current = key
      setSaveState('saved')
    } catch (e) {
      Error.showError(e)
      setSaveState('error')
    }
  }

  const handleReorderStart = () => {
    isReorderingRef.current = true
  }

  const handleReorderEnd = () => {
    isReorderingRef.current = false
    saveQuestJourney(mediasListRef.current)
  }

  useEffect(() => {
    if (initialLoadRef.current) {
      initialLoadRef.current = false
      return
    }
    if (isMediaFetching || isReorderingRef.current) {
      return
    }
    saveQuestJourney(debouncedMedias)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedMedias])

  useEffect(() => {
    const getQuestResources = async () => {
      try {
        const { data } = await API.AdminAPI.quests.getQuestResources(questId)
        const normalize = (item) => {
          if (!item) return item
          const id = item._id || item.id
          return id ? { ...item, _id: id, id } : item
        }
        setResource({
          videos: (data.videos || []).map(normalize),
          blogs: (data.blogs || []).map(normalize),
        })
        setIsLoading(false)
      } catch (e) {
        Error.showError(e)
        setIsLoading(false)
      }
    }
    getQuestResources()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questId])

  const refetchResources = async () => {
    const { data } = await API.AdminAPI.quests.getQuestResources(questId)
    const normalize = (item) => {
      if (!item) return item
      const id = item._id || item.id
      return id ? { ...item, _id: id, id } : item
    }
    const next = {
      videos: (data.videos || []).map(normalize),
      blogs: (data.blogs || []).map(normalize),
    }
    setResource(next)
    return next
  }

  // Keep Build rail "Learning Resource" note in sync (total listen+watch+read).
  useEffect(() => {
    if (isLoading || isMediaFetching) return
    if (typeof onCompletenessChange !== 'function') return
    const audios = mediasList || []
    const videos = resource?.videos || []
    const blogs = resource?.blogs || []
    onCompletenessChange({
      audios,
      journeyAudios: audios,
      videos,
      blogs,
    })
    // onCompletenessChange is an inline parent setter — omit from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediasList, resource, isLoading, isMediaFetching])

  if (isLoading || isMediaFetching) {
    return <ABSpinner />
  }

  const mediaId = (item) => item?._id || item?.id

  const removeVideo = (id) => {
    setResource((prev) => ({
      ...prev,
      videos: (prev.videos || []).filter((v) => mediaId(v) !== id),
    }))
  }

  const addVideo = (video) => {
    const id = mediaId(video)
    setResource((prev) => {
      const existing = prev.videos || []
      if (id && existing.some((v) => mediaId(v) === id)) {
        return prev
      }
      return {
        ...prev,
        videos: [...existing, video],
      }
    })
  }

  const handleVideoUpdate = (updatedVideos) => {
    setResource((prev) => ({
      ...prev,
      videos: (updatedVideos || []).filter((video) => mediaId(video)),
    }))
  }

  const addBlog = (blog) => {
    const id = mediaId(blog)
    setResource((prev) => {
      const existing = prev.blogs || []
      if (id && existing.some((b) => mediaId(b) === id)) {
        return prev
      }
      return {
        ...prev,
        blogs: [...existing, blog],
      }
    })
  }

  const handleBlogUpdate = (updatedBlogs) => {
    setResource((prev) => ({
      ...prev,
      blogs: (updatedBlogs || []).filter((blog) => mediaId(blog)),
    }))
  }

  const listenCount = mediasList?.length || 0
  const watchCount = resource?.videos?.length || 0
  const readCount = resource?.blogs?.length || 0

  return (
    <JourneyMediasContext.Provider
      value={{
        mediasList,
        setMediasList,
        onReorderStart: handleReorderStart,
        onReorderEnd: handleReorderEnd,
      }}>
      <div className='experience-mini-tabs'>
        <button
          type='button'
          className={activeTab === 'listen' ? 'sel' : undefined}
          onClick={() => setActiveTab('listen')}>
          Listen · {listenCount}
        </button>
        <button
          type='button'
          className={activeTab === 'watch' ? 'sel' : undefined}
          onClick={() => setActiveTab('watch')}>
          Watch · {watchCount}
        </button>
        <button
          type='button'
          className={activeTab === 'read' ? 'sel' : undefined}
          onClick={() => setActiveTab('read')}>
          Read · {readCount}
        </button>
      </div>

      {activeTab === 'listen' && (
        <LearningListenPicker
          questName={questName}
          mediasList={mediasList}
          setMediasList={setMediasList}
        />
      )}

      {activeTab === 'watch' && (
        <QuestVideos
          layout='picker'
          videos={resource?.videos}
          addVideo={addVideo}
          removeVideo={removeVideo}
          onVideoUpdate={handleVideoUpdate}
          onSaveStateChange={onSaveStateChange}
          onRefetchResources={refetchResources}
        />
      )}

      {activeTab === 'read' && (
        <QuestBlogs
          layout='picker'
          blogs={resource?.blogs}
          addBlog={addBlog}
          onBlogUpdate={handleBlogUpdate}
          onSaveStateChange={onSaveStateChange}
          onRefetchResources={refetchResources}
        />
      )}
    </JourneyMediasContext.Provider>
  )
}

export default QuestsLearning
